import { parseISO } from 'date-fns';

// ==================== TIMESTAMP PARSING ====================

/**
 * Parse timestamps in various formats (ISO, RFC 2822, etc.)
 */
export function parseTimestamp(timestamp: string): Date | null {
  if (!timestamp) return null;

  // Try ISO format first
  try {
    const isoDate = parseISO(timestamp);
    if (!isNaN(isoDate.getTime())) {
      return isoDate;
    }
  } catch (e) {
    // Continue to try other formats
  }

  // Try parsing RFC 2822 format (e.g., "Mon, 31 Mar 2025 05:00:00 GMT")
  try {
    const rfcDate = new Date(timestamp);
    if (!isNaN(rfcDate.getTime())) {
      return rfcDate;
    }
  } catch (e) {
    // Continue to next format
  }

  return null;
}

/**
 * Get timestamp field from data item (handles multiple field names)
 */
export function getTimestamp(item: any): string {
  return item.timestamp || item.fecha || '';
}

// ==================== VALUE EXTRACTION ====================

/**
 * Get consumption value from data item (handles multiple field names)
 */
export function getConsumptionValue(item: any): number {
  return item.consumption || item.consumo_kwh || item.energy_kwh || 0;
}

/**
 * Get price value from data item (handles multiple field names)
 */
export function getPriceValue(item: any): number {
  return item.priceUsd || item.price_usd || item.price_usd_per_mwh || 0;
}

/**
 * Generic value getter that handles multiple field names
 */
export function getValue(data: any, valueKey: string, fallbackKey?: string): number {
  if (valueKey === 'priceUsd') return getPriceValue(data);
  if (valueKey === 'consumption') return getConsumptionValue(data);

  // For demand/power data, try multiple field names
  if (valueKey === 'power_kw' || valueKey === 'demand_kw') {
    return data.power_kw || data.demand_kw || data.power || 0;
  }

  // Try primary key first, then fallback key
  return data[valueKey] || (fallbackKey ? data[fallbackKey] : 0) || 0;
}

// ==================== DATE RANGE HELPERS ====================

/**
 * Parse and validate data with timestamps
 */
export function parseValidData(data: any[]): Array<any & { date: Date; timestamp: string }> {
  return data
    .map(d => {
      const timestamp = getTimestamp(d);
      const date = parseTimestamp(timestamp);
      if (!date || isNaN(date.getTime())) return null;
      return { ...d, date, timestamp };
    })
    .filter(d => d !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Detect data interval (hourly, 15-minute, etc.)
 */
export function detectDataInterval(validData: any[]): number {
  let dataIntervalHours = 1;
  if (validData.length > 1) {
    const timeDiff = (validData[1].date.getTime() - validData[0].date.getTime()) / (1000 * 60 * 60);
    if (timeDiff < 1) {
      dataIntervalHours = timeDiff; // e.g., 0.25 for 15-minute data
    }
  }
  return dataIntervalHours;
}

/**
 * Calculate date range coverage
 */
export function calculateCoverage(validData: any[]): {
  startDate: Date | null;
  endDate: Date | null;
  coverage: number;
  totalDays: number;
  missingDays: number;
} {
  if (validData.length === 0) {
    return {
      startDate: null,
      endDate: null,
      coverage: 0,
      totalDays: 0,
      missingDays: 0
    };
  }

  const startDate = validData[0].date;
  const endDate = validData[validData.length - 1].date;

  const daySpan = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const uniqueDays = new Set(validData.map(d => d.date.toDateString())).size;
  const coverage = (uniqueDays / daySpan) * 100;

  return {
    startDate,
    endDate,
    coverage,
    totalDays: daySpan,
    missingDays: daySpan - uniqueDays
  };
}

// ==================== PATTERN ANALYSIS ====================

/**
 * Calculate hourly pattern from data
 */
export function calculateHourlyPattern(
  validData: any[],
  dataIntervalHours: number,
  valueKey: string,
  fallbackKey?: string
): { hourlyPattern: number[]; hourlyStdDev: number[] } {
  const hourlyData: { [key: number]: number[] } = {};
  for (let h = 0; h < 24; h++) {
    hourlyData[h] = [];
  }

  // For sub-hourly data, aggregate by hour first
  if (dataIntervalHours < 1) {
    const hourlyAggregated: { [key: string]: number } = {};

    validData.forEach(d => {
      const hour = d.date.getHours();
      const dateKey = `${d.date.toDateString()}_${hour}`;
      if (!hourlyAggregated[dateKey]) hourlyAggregated[dateKey] = 0;

      const value = getValue(d, valueKey, fallbackKey);
      hourlyAggregated[dateKey] += value;
    });

    // Group by hour across all days
    Object.entries(hourlyAggregated).forEach(([key, value]) => {
      const hour = parseInt(key.split('_')[1]);
      hourlyData[hour].push(value);
    });
  } else {
    // For hourly data, just collect values by hour
    validData.forEach(d => {
      const hour = d.date.getHours();
      const value = getValue(d, valueKey, fallbackKey);
      hourlyData[hour].push(value);
    });
  }

  // Calculate averages
  const hourlyPattern = Object.keys(hourlyData).map(h => {
    const values = hourlyData[parseInt(h)];
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  });

  // Calculate standard deviation for each hour
  const hourlyStdDev = Object.keys(hourlyData).map(h => {
    const values = hourlyData[parseInt(h)];
    if (values.length <= 1) return 0;

    const mean = hourlyPattern[parseInt(h)];
    const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
    const variance = squaredDiffs.reduce((a, b) => a + b, 0) / (values.length - 1);
    return Math.sqrt(variance);
  });

  return { hourlyPattern, hourlyStdDev };
}

/**
 * Calculate weekday vs weekend averages
 */
export function calculateWeekdayWeekendAverages(
  validData: any[],
  dataIntervalHours: number,
  valueKey: string,
  fallbackKey?: string
): { weekdayAverage: number; weekendAverage: number } {
  const weekdayData = validData.filter(d => {
    const dayOfWeek = d.date.getDay();
    return dayOfWeek >= 1 && dayOfWeek <= 5;
  });

  const weekendData = validData.filter(d => {
    const dayOfWeek = d.date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  });

  const calculateDailyAverage = (dayData: any[]) => {
    if (dayData.length === 0) return 0;

    // Group by date
    const dailyTotals: { [key: string]: number } = {};
    dayData.forEach(d => {
      const dateKey = d.date.toDateString();
      if (!dailyTotals[dateKey]) dailyTotals[dateKey] = 0;
      const value = getValue(d, valueKey, fallbackKey);
      dailyTotals[dateKey] += value;
    });

    const dailyValues = Object.values(dailyTotals);
    return dailyValues.reduce((sum, val) => sum + val, 0) / dailyValues.length;
  };

  const weekdayAverage = dataIntervalHours < 1
    ? calculateDailyAverage(weekdayData)
    : weekdayData.length > 0
      ? weekdayData.reduce((sum, d) => sum + getValue(d, valueKey, fallbackKey), 0) / weekdayData.length * 24
      : 0;

  const weekendAverage = dataIntervalHours < 1
    ? calculateDailyAverage(weekendData)
    : weekendData.length > 0
      ? weekendData.reduce((sum, d) => sum + getValue(d, valueKey, fallbackKey), 0) / weekendData.length * 24
      : 0;

  return { weekdayAverage, weekendAverage };
}

/**
 * Calculate seasonal averages
 */
export function calculateSeasonalAverages(
  validData: any[],
  dataIntervalHours: number,
  valueKey: string,
  fallbackKey?: string
): { winter: number; spring: number; summer: number; fall: number } {
  const calculateSeasonalAverage = (seasonData: any[]) => {
    if (seasonData.length === 0) return 0;

    if (dataIntervalHours < 1) {
      // For sub-hourly data, calculate daily totals first
      const dailyTotals: { [key: string]: number } = {};
      seasonData.forEach(d => {
        const dateKey = d.date.toDateString();
        if (!dailyTotals[dateKey]) dailyTotals[dateKey] = 0;
        const value = getValue(d, valueKey, fallbackKey);
        dailyTotals[dateKey] += value;
      });

      const dailyValues = Object.values(dailyTotals);
      return dailyValues.reduce((sum, val) => sum + val, 0) / dailyValues.length;
    } else {
      // For hourly data, calculate average and multiply by 24 for daily
      const values = seasonData.map(d => getValue(d, valueKey, fallbackKey));
      return values.reduce((a, b) => a + b, 0) / values.length * 24;
    }
  };

  const winterData = validData.filter(d => {
    const month = d.date.getMonth();
    return month >= 11 || month <= 1;
  });

  const springData = validData.filter(d => {
    const month = d.date.getMonth();
    return month >= 2 && month <= 4;
  });

  const summerData = validData.filter(d => {
    const month = d.date.getMonth();
    return month >= 5 && month <= 7;
  });

  const fallData = validData.filter(d => {
    const month = d.date.getMonth();
    return month >= 8 && month <= 10;
  });

  return {
    winter: calculateSeasonalAverage(winterData),
    spring: calculateSeasonalAverage(springData),
    summer: calculateSeasonalAverage(summerData),
    fall: calculateSeasonalAverage(fallData)
  };
}
